import { FC, useMemo, useState } from 'react';
import {
  Text,
  SimpleGrid,
  Stack,
  Group,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { LabeledField } from '@/components/LabeledField';
import { getSummaryLines } from '../utils/pre-implantation.utils';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import DeleteConfirmModal from '../../images-tab/DeleteModal';
import { ListContentAction } from '../utils/medical-history.types';

export interface BirthPeriodListContentProps {
  periodType: 'perinatalPeriod' | 'prenatalPeriod';
  questionnaire: Questionnaire | undefined;
  questionnaireResponse: QuestionnaireResponse | null | undefined;
  hasData: boolean | undefined | null;
  onAction?: (action: ListContentAction) => void;
  readonly?: boolean;
}

export const BirthPeriodListContent: FC<BirthPeriodListContentProps> = ({
  periodType,
  questionnaire,
  questionnaireResponse,
  hasData,
  onAction = () => {},
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages']);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  const summaryLines = useMemo(
    () => getSummaryLines(periodType, questionnaire, questionnaireResponse),
    [periodType, questionnaire, questionnaireResponse]
  );

  const handleDeleteClick = () => {
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteModalOpened(false);
    onAction('remove');
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpened(false);
  };

  return (
    <>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap="xs" flex={1}>
          <Text fw={600} size="sm">
            {t(`medicalHistory.${periodType}.title`)}
          </Text>
          {!hasData ? (
            <Text size="sm" c="dimmed" ta="center">
              {t(`medicalHistory.${periodType}.noData`, {
                defaultValue: `No ${periodType} data recorded`,
              })}
            </Text>
          ) : (
            <>
              {summaryLines.length > 0 && (
                <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
                  {summaryLines.map((line, index) => (
                    <LabeledField
                      key={index}
                      label={line.label}
                      value={line.value}
                    />
                  ))}
                </SimpleGrid>
              )}
            </>
          )}
        </Stack>

        {/* Actions menu or loading indicator */}
        {hasData && !readonly && (
          <Menu shadow="md" width={160} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => onAction('edit')}
              >
                {t('edit', {
                  ns: 'common',
                  defaultValue: 'Edit',
                })}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                onClick={handleDeleteClick}
                color="red"
              >
                {t('delete', {
                  ns: 'common',
                  defaultValue: 'Delete',
                })}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t(`medicalHistory.${periodType}.confirmDelete.title`, {
          ns: 'patient-pages',
          defaultValue:
            periodType === 'prenatalPeriod'
              ? 'Delete prenatal period data?'
              : 'Delete perinatal period data?',
        })}
        message={t(`medicalHistory.${periodType}.confirmDelete.message`, {
          ns: 'patient-pages',
          defaultValue:
            periodType === 'prenatalPeriod'
              ? 'Are you sure you want to delete this prenatal period data? This action cannot be undone.'
              : 'Are you sure you want to delete this perinatal period data? This action cannot be undone.',
        })}
      />
    </>
  );
};

export default BirthPeriodListContent;
